import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { documentsAPI } from "../../services/api";

const isHtmlDoc = (d) => {
  const c = (d?.content || "").toString().trim();

  // Trust the content first
  if (/^<!doctype\s+html/i.test(c)) return true;
  if (/^<html[\s>]/i.test(c)) return true;
  if (/<head[\s>]/i.test(c) && /<body[\s>]/i.test(c)) return true;

  // Fallback to declared contentType (if your backend sets it)
  const t = (d?.contentType || "").toString().toLowerCase();
  return t.includes("html");
};

const normalizeKey = (v) => (v ?? "").toString().trim();

const LAST_READ_KEY = "sourceMaterial:lastReadById";

const loadLastReadMap = () => {
  try {
    const raw = localStorage.getItem(LAST_READ_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const saveLastReadMap = (map) => {
  try {
    localStorage.setItem(LAST_READ_KEY, JSON.stringify(map || {}));
  } catch {
    // ignore
  }
};

const toTime = (v) => {
  const t = new Date(v || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

const SourceMaterial = () => {
  const [categories, setCategories] = useState([]);
  const [allDocs, setAllDocs] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);

  const [selectedDocId, setSelectedDocId] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);

  const [search, setSearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());
  const [error, setError] = useState(null);

  // Unread / Updated tracking
  const [lastReadById, setLastReadById] = useState(() => loadLastReadMap());

  // For auto-expand-on-search
  const userExpandedRef = useRef(new Set());
  const autoExpandActiveRef = useRef(false);

  const iframeRef = useRef(null);

  // Persist lastRead map
  useEffect(() => {
    saveLastReadMap(lastReadById);
  }, [lastReadById]);

  // Load categories + docs to build the sidebar
  useEffect(() => {
    const run = async () => {
      try {
        setLoadingTree(true);
        setError(null);

        const catRes = await documentsAPI.getCategories();
        const catRows = catRes.data || [];
        const cats = catRows.map((r) => r.category).filter(Boolean);

        setCategories(cats);

        // Fetch docs per category (API supports category fetch)
        const results = await Promise.all(
          cats.map(async (cat) => {
            try {
              const res = await documentsAPI.getDocumentsByCategory(cat);
              return (res.data || []).map((d) => ({
                ...d,
                category: d.category ?? cat,
              }));
            } catch (e) {
              console.error(`Failed to fetch docs for category "${cat}"`, e);
              return [];
            }
          })
        );

        const merged = results.flat();
        setAllDocs(merged);

        // Default expand first category + seed restore snapshot
        if (cats.length > 0) {
          const initial = new Set([cats[0]]);
          setExpandedCategories(initial);
          userExpandedRef.current = new Set(initial);
        }

        // Default select first doc
        if (merged.length > 0) {
          setSelectedDocId((prev) => prev ?? merged[0].id);
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load Source Material. Check /api/documents endpoints.");
      } finally {
        setLoadingTree(false);
      }
    };

    run();
  }, []);

  // Load selected doc content
  useEffect(() => {
    if (!selectedDocId) return;

    const run = async () => {
      try {
        setLoadingDoc(true);
        setError(null);

        const res = await documentsAPI.getDocument(selectedDocId);
        setDoc(res.data || null);
      } catch (e) {
        console.error(e);
        setError("Failed to load document. Is /api/documents/:id working?");
      } finally {
        setLoadingDoc(false);
      }
    };

    run();
  }, [selectedDocId]);

  // Mark document as read when it is loaded
  useEffect(() => {
    if (!doc?.id) return;

    const stamp = doc.lastUpdated || new Date().toISOString();

    setLastReadById((prev) => {
      if (prev?.[doc.id] === stamp) return prev;
      return { ...(prev || {}), [doc.id]: stamp };
    });
  }, [doc?.id, doc?.lastUpdated]);

  const handleOpenHtmlInNewTab = () => {
    if (!doc?.content) return;

    const blob = new Blob([doc.content], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  const htmlMode = isHtmlDoc(doc);

  // Build: category -> docs[]
  const tree = useMemo(() => {
    const q = search.trim().toLowerCase();

    const byCat = new Map();

    for (const d of allDocs) {
      const name = (d?.name || "").toString();
      if (q && !name.toLowerCase().includes(q)) continue;

      const cat = normalizeKey(d?.category) || "Uncategorized";
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat).push(d);
    }

    // Sort docs alphabetically
    const sortByName = (a, b) => (a?.name || "").localeCompare(b?.name || "");

    const output = [];
    for (const [cat, docs] of byCat.entries()) {
      docs.sort(sortByName);
      output.push({ category: cat, docs, count: docs.length });
    }

    // Prefer category order from API
    const catIndex = new Map(categories.map((c, i) => [c, i]));
    output.sort((a, b) => {
      const ai = catIndex.has(a.category) ? catIndex.get(a.category) : 9999;
      const bi = catIndex.has(b.category) ? catIndex.get(b.category) : 9999;
      if (ai !== bi) return ai - bi;
      return a.category.localeCompare(b.category);
    });

    return output;
  }, [allDocs, categories, search]);

  // Auto-expand categories when searching; restore when search clears
  // NOTE: Does not reference expandedCategories to satisfy react-hooks/exhaustive-deps
  useEffect(() => {
    const q = search.trim();

    if (q.length > 0) {
      if (!autoExpandActiveRef.current) {
        autoExpandActiveRef.current = true;
      }

      const catsWithMatches = new Set(tree.map((n) => n.category));
      setExpandedCategories(catsWithMatches);
      return;
    }

    if (autoExpandActiveRef.current) {
      setExpandedCategories(new Set(userExpandedRef.current));
      autoExpandActiveRef.current = false;
    }
  }, [search, tree]);

  const toggleCategory = (cat) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);

      // Only store user expansion when not actively searching
      if (search.trim().length === 0) {
        userExpandedRef.current = new Set(next);
      }

      return next;
    });
  };

  return (
    <div className="source-material-layout">
      {/* Sidebar */}
      <aside className="source-material-sidebar">
        <div className="source-material-sidebarHeader">
          <h2 className="source-material-title">Source Material</h2>

          <input
            className="source-material-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents…"
          />
        </div>

        {loadingTree ? (
          <p className="source-material-muted">Loading…</p>
        ) : error ? (
          <p className="source-material-error">{error}</p>
        ) : tree.length === 0 ? (
          <p className="source-material-muted">No documents found.</p>
        ) : (
          <nav className="source-material-tree" aria-label="Source Material Categories">
            {tree.map((catNode) => {
              const cat = catNode.category;
              const catOpen = expandedCategories.has(cat);

              return (
                <div key={cat} className="source-material-node">
                  <button
                    type="button"
                    className={`source-material-row source-material-categoryRow ${
                      catOpen ? "is-open" : ""
                    }`}
                    onClick={() => toggleCategory(cat)}
                    aria-expanded={catOpen}
                  >
                    <span className="source-material-caret" aria-hidden="true">
                      {catOpen ? "▾" : "▸"}
                    </span>
                    <span className="source-material-label">{cat}</span>
                    <span className="source-material-count">{catNode.count}</span>
                  </button>

                  {catOpen ? (
                    <div className="source-material-children">
                      {catNode.docs.map((d) => {
                        const lastRead = lastReadById?.[d.id];
                        const lastReadTime = toTime(lastRead);
                        const lastUpdatedTime = toTime(d.lastUpdated);

                        const isUnread = !lastRead;
                        const isUpdated = !!lastRead && lastUpdatedTime > lastReadTime;

                        const statusClass = isUnread
                          ? "is-unread"
                          : isUpdated
                          ? "is-updated"
                          : "";

                        return (
                          <button
                            key={d.id}
                            type="button"
                            className={`source-material-row source-material-docRow ${
                              selectedDocId === d.id ? "is-active" : ""
                            }`}
                            onClick={() => setSelectedDocId(d.id)}
                            title={d.name}
                          >
                            <span className="source-material-docDot" aria-hidden="true">
                              •
                            </span>

                            <span
                              className={`source-material-statusDot ${statusClass}`}
                              aria-hidden="true"
                            />

                            <span className="source-material-docName">{d.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>
        )}
      </aside>

      {/* Content */}
      <section className="source-material-content">
        {loadingDoc ? (
          <p className="source-material-muted">Loading…</p>
        ) : !doc ? (
          <p className="source-material-muted">Select a document.</p>
        ) : (
          <>
            <div className="source-material-toolbar">
              <h1 className="source-material-docTitle">{doc.name}</h1>

              {htmlMode ? (
                <button
                  type="button"
                  className="source-material-toolbarBtn"
                  onClick={handleOpenHtmlInNewTab}
                >
                  Open in new tab
                </button>
              ) : null}
            </div>

            <div className="source-material-body">
              {htmlMode ? (
                <iframe
                  ref={iframeRef}
                  title={doc.name}
                  srcDoc={doc.content || ""}
                  className="source-material-iframe"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-modals"
                />
              ) : (
                <div className="source-material-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {doc.content || ""}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default SourceMaterial;
